import { resolutionStatus } from '../components/resolutionStatus'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { Bot, Check, Loader2, Play } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { DailyBars } from '../components/DailyBars'
import { KnowledgeFrame } from '../components/KnowledgeNav'
import { useAgentStats, useBulkJobs, useCancelBulkJob, useRunAutopilot, useSetAutopilotPolicy } from '../hooks/useKnowledge'
import type { AgentStats, AutopilotPolicy, BulkJob, ProposalAction } from '../api/knowledgeApi'

const num = (v: number | null | undefined) => (v == null ? '—' : Math.round(v).toLocaleString())
const pct = (v: number | null | undefined) => (v == null ? '—' : `${Math.round(v * 100)}%`)
const usd = (v: number | null | undefined, digits = 2) => (v == null ? '—' : `$${v.toFixed(digits)}`)

const ACTION_LABEL: Record<ProposalAction, string> = {
  merge: 'Merges', split: 'Splits', link: 'Relations', category: 'Categories', generic: 'Generic', reject: 'Not concepts',
}
const ACTION_WHAT: Record<string, string> = {
  category: 'gives an uncategorised concept a category',
  generic: 'down-weights a concept that says little about an artwork',
  merge: 'merges concepts into one on the next build',
  reject: 'removes a label that is not a real concept on the next build',
}

// Who decided a proposal. Validated as a categorical set (light surface, CVD-separated in this order).
const OUTCOME = {
  person: { label: 'Accepted one by one', color: '#1d4ed8' },
  bulk: { label: 'Accepted in bulk', color: '#f59e0b' },
  autopilot: { label: 'Accepted by autopilot', color: '#0d9488' },
  rejected: { label: 'Rejected', color: '#be123c' },
}

// What is waiting, by what will happen to it.
const QUEUE = [
  { key: 'eligible', label: 'Autopilot takes on its next pass', color: '#0d9488' },
  { key: 'eligibleIfEnabled', label: 'Would be taken if its policy were on', color: '#5eead4' },
  { key: 'held', label: 'Held: contradicts another proposal', color: '#f59e0b' },
  { key: 'below', label: 'Below the threshold', color: '#cbd5e1' },
  { key: 'manualOnly', label: 'Relations & splits: a person decides', color: '#1d4ed8' },
] as const

const LEVELS = [0.99, 0.98, 0.97, 0.96, 0.95, 0.9, 0.85, 0.8]

function Tile({ label, value, hint }: { label: string; value: string; hint: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="space-y-1 p-4">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
        <div className="text-[11px] leading-snug text-slate-500">{hint}</div>
      </CardContent>
    </Card>
  )
}

/** The status line at the top: what autopilot is doing right now. */
function AutopilotBanner({ stats, jobs }: { stats: AgentStats; jobs: BulkJob[] }) {
  const on = stats.policies.filter((p) => p.enabled)
  const open = jobs.filter((j) => j.status === 'queued' || j.status === 'running')
  const cancel = useCancelBulkJob()
  const running = stats.runs.find((r) => resolutionStatus(r) === 'running')
  return (
    <Card className={on.length ? 'border-teal-200 bg-teal-50' : 'border-amber-200 bg-amber-50'}>
      <CardContent className="space-y-2 p-4 text-sm">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Bot className={`h-4 w-4 ${on.length ? 'text-teal-700' : 'text-amber-700'}`} />
          {on.length ? (
            <span className="font-medium text-teal-900">
              Autopilot is on for {on.map((p) => `${ACTION_LABEL[p.action].toLowerCase()} ≥ ${p.minConfidence.toFixed(2)}`).join(', ')}.
            </span>
          ) : (
            <span className="font-medium text-amber-900">
              Autopilot is off — nothing is accepted without a person. {num(stats.coverage.eligibleIfEnabled)} waiting proposals
              are above the recommended thresholds.
            </span>
          )}
          <span className="text-slate-600">
            It runs after every proposal pull (hourly, and after the nightly build). Contradicting proposals always wait for a person.
          </span>
        </div>
        {running && (
          <div className="flex items-center gap-2 text-violet-800">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Resolution run in progress · {running.model} · {num(running.verdicts)} verdicts so far · {usd(running.spentUsd)} of {usd(running.budgetUsd)}
          </div>
        )}
        {!running && stats.runs[0] && resolutionStatus(stats.runs[0]) === 'stale' && (
          <p className="text-amber-800">The latest resolution run has no recent heartbeat. Its stored “running” flag is stale; check the Knowledge garden for execution and import status.</p>
        )}
        {stats.runs[0]?.status === 'provider_blocked' && <p className="text-amber-800">New model analysis is blocked by the provider. Existing proposals can still be reviewed or accepted by autopilot.</p>}
        {open.map((j) => (
          <div key={j.id} className="space-y-1">
            <div className="flex items-center gap-2 text-xs text-slate-700">
              <Loader2 className="h-3 w-3 animate-spin" />
              {j.origin === 'autopilot' ? 'Autopilot' : 'Bulk accept'} · {ACTION_LABEL[j.action]} ≥ {j.minConfidence.toFixed(2)} ·
              {' '}{num(j.accepted)} of {num(j.total)}
              <button className="underline hover:text-slate-900" disabled={cancel.isPending} onClick={() => cancel.mutate(j.id)}>Stop</button>
            </div>
            <div className="h-1.5 overflow-hidden rounded bg-white">
              <div className="h-full bg-teal-600 transition-all" style={{ width: `${Math.min(100, (100 * j.accepted) / Math.max(1, j.total))}%` }} />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

/** The waiting queue as one segmented bar, plus "accept all" and "turn it all on". */
function QueueCard({ stats }: { stats: AgentStats }) {
  const run = useRunAutopilot()
  const setPolicy = useSetAutopilotPolicy()
  const [confirming, setConfirming] = useState(false)
  const total = QUEUE.reduce((t, q) => t + stats.coverage[q.key], 0)
  const takeable = stats.coverage.eligible + stats.coverage.eligibleIfEnabled
  const off = stats.policies.filter((p) => !p.enabled)

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">What is waiting — {num(total)} proposals</CardTitle>
        <CardDescription>Every pending proposal, by what will happen to it at the current thresholds.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {total > 0 ? (
          <div className="flex h-4 gap-[2px] overflow-hidden rounded">
            {QUEUE.map((q) => {
              const v = stats.coverage[q.key]
              return v > 0 ? (
                <div key={q.key} title={`${q.label}: ${v.toLocaleString()}`} style={{ width: `${(100 * v) / total}%`, background: q.color }} className="min-w-[3px]" />
              ) : null
            })}
          </div>
        ) : (
          <p className="text-sm text-slate-500">The queue is empty.</p>
        )}
        <div className="grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2 lg:grid-cols-3">
          {QUEUE.map((q) => (
            <span key={q.key} className="inline-flex items-center gap-1.5 text-slate-600">
              <span className="h-2 w-2 rounded-sm" style={{ background: q.color }} />
              {q.label}: <b className="tabular-nums text-slate-900">{num(stats.coverage[q.key])}</b>
            </span>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t pt-3">
          {takeable > 0 && (confirming ? (
            <>
              <Button size="sm" disabled={run.isPending} onClick={() => run.mutate(true, { onSettled: () => setConfirming(false) })}>
                <Check className="mr-1.5 h-3.5 w-3.5" /> Confirm — accept {num(takeable)} now
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>Cancel</Button>
            </>
          ) : (
            <Button size="sm" onClick={() => setConfirming(true)}>
              <Check className="mr-1.5 h-3.5 w-3.5" /> Accept all {num(takeable)} at these thresholds
            </Button>
          ))}
          {off.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              disabled={setPolicy.isPending}
              onClick={() => off.forEach((p) => setPolicy.mutate({ action: p.action, change: { enabled: true } }))}
            >
              <Bot className="mr-1.5 h-3.5 w-3.5" /> Turn autopilot on for {off.length === 4 ? 'everything' : off.map((p) => ACTION_LABEL[p.action].toLowerCase()).join(', ')}
            </Button>
          )}
          {stats.coverage.eligible > 0 && (
            <Button size="sm" variant="ghost" disabled={run.isPending} onClick={() => run.mutate(false)}>
              <Play className="mr-1.5 h-3.5 w-3.5" /> Run autopilot now
            </Button>
          )}
          <span className="text-xs text-slate-500">
            Accepted merges and removals apply on the next nightly build; any of them can be revoked under{' '}
            <Link to="/app/knowledge/rules" className="underline">Rules</Link>.
          </span>
        </div>
      </CardContent>
    </Card>
  )
}

/** Pending proposals of one action by confidence, the policy's threshold marked. */
function BandStrip({ bands, threshold }: { bands: Array<{ band: number; n: number }>; threshold: number }) {
  const total = bands.reduce((t, b) => t + b.n, 0)
  if (!total) return <span className="text-xs text-slate-400">none waiting</span>
  return (
    <div className="flex h-2.5 w-40 gap-px overflow-hidden rounded-sm" aria-label="Pending by confidence">
      {bands.map((b) => (
        <div
          key={b.band}
          title={`${b.band.toFixed(2)} × ${b.n.toLocaleString()}${b.band >= threshold - 1e-4 ? ' (accepted by the policy)' : ''}`}
          style={{ width: `${(100 * b.n) / total}%`, background: b.band >= threshold - 1e-4 ? '#0d9488' : '#cbd5e1' }}
          className="min-w-[2px]"
        />
      ))}
    </div>
  )
}

function PolicyRow({ policy, stats }: { policy: AutopilotPolicy; stats: AgentStats }) {
  const setPolicy = useSetAutopilotPolicy()
  const outcomes = stats.byAction.find((a) => a.action === policy.action)
  const bands = stats.bands.filter((b) => b.action === policy.action)
  const levels = [...new Set([...LEVELS, policy.minConfidence])].sort((a, b) => b - a)
  const change = (c: { enabled?: boolean; minConfidence?: number }) => setPolicy.mutate({ action: policy.action, change: c })
  return (
    <TableRow>
      <TableCell>
        <Link to={`/app/knowledge/proposals?action=${policy.action}`} className="font-medium hover:underline">{ACTION_LABEL[policy.action]}</Link>
        <div className="text-[11px] text-slate-500">{ACTION_WHAT[policy.action]}</div>
      </TableCell>
      <TableCell>
        <Switch checked={policy.enabled} disabled={setPolicy.isPending} onCheckedChange={(v) => change({ enabled: v })} aria-label={`Autopilot for ${policy.action}`} />
      </TableCell>
      <TableCell>
        <select
          className="rounded border bg-white px-2 py-1 text-sm tabular-nums"
          value={policy.minConfidence}
          disabled={setPolicy.isPending}
          onChange={(e) => change({ minConfidence: Number(e.target.value) })}
          aria-label={`Threshold for ${policy.action}`}
        >
          {levels.map((v) => <option key={v} value={v}>≥ {v.toFixed(2)}</option>)}
        </select>
      </TableCell>
      <TableCell><BandStrip bands={bands} threshold={policy.minConfidence} /></TableCell>
      <TableCell className="text-right tabular-nums">{num(policy.pending)}</TableCell>
      <TableCell className="text-right tabular-nums font-medium">{num(policy.eligible)}</TableCell>
      <TableCell className="text-right tabular-nums text-amber-700">{policy.held ? num(policy.held) : '—'}</TableCell>
      <TableCell className="text-right tabular-nums">{num(outcomes?.autopilot)}</TableCell>
      <TableCell className="text-right tabular-nums" title={outcomes ? `${outcomes.person} accepted, ${outcomes.rejected} rejected one by one` : undefined}>
        {pct(outcomes?.precision)}
        {outcomes && outcomes.reviewed > 0 && <span className="ml-1 text-[10px] text-slate-400">of {num(outcomes.reviewed)}</span>}
      </TableCell>
    </TableRow>
  )
}

export function AgentPage() {
  const [days, setDays] = useState(30)
  const { data: stats, isLoading } = useAgentStats(days)
  const { data: jobs } = useBulkJobs()

  if (isLoading || !stats) {
    return (
      <KnowledgeFrame title="Resolution agent" description="What the resolution model does to the graph, and how much of it is accepted automatically.">
        <Skeleton className="h-96 w-full" />
      </KnowledgeFrame>
    )
  }
  const t = stats.totals
  const accepted = stats.byAction.reduce(
    (o, a) => ({ person: o.person + a.person, bulk: o.bulk + a.bulk, autopilot: o.autopilot + a.autopilot, rejected: o.rejected + a.rejected }),
    { person: 0, bulk: 0, autopilot: 0, rejected: 0 }
  )
  const decided = accepted.person + accepted.bulk + accepted.autopilot + accepted.rejected

  return (
    <KnowledgeFrame
      title="Resolution agent"
      description={
        <>
          After each nightly build a frontier model reviews groups of related concepts and proposes merges, splits,
          relations, categories and removals. This page shows what it costs and produces, who decided its proposals —
          a person one by one, a person in bulk, or autopilot — and the policies that accept the confident ones
          without anyone clicking.
        </>
      }
    >
      <AutopilotBanner stats={stats} jobs={jobs?.data ?? stats.jobs} />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        <Tile
          label="Resolution runs"
          value={num(t.runs)}
          hint={t.lastFinishedAt ? `last ${formatDistanceToNow(new Date(t.lastFinishedAt), { addSuffix: true })}` : 'none yet'}
        />
        <Tile label="Verdicts" value={num(t.verdicts)} hint={`${num(t.verdictsWindow)} in the last ${days} days · ${num(t.proposals)} became proposals`} />
        <Tile label="Spent" value={usd(t.spentUsd)} hint={`${usd(t.spentUsdWindow)} in ${days} days · ${t.costPerVerdict != null ? `${usd(t.costPerVerdict, 4)} a verdict` : '—'}`} />
        <Tile
          label="Calibration held"
          value={pct(t.calibrationPassRate)}
          hint={`${num(t.calibratedRuns)} of ${num(t.workingRuns)} working runs caught enough planted cases to keep their proposals`}
        />
        <Tile
          label="Decided"
          value={num(decided)}
          hint={`${pct(decided ? accepted.autopilot / decided : null)} by autopilot · ${pct(decided ? accepted.rejected / decided : null)} rejected`}
        />
        <Tile
          label="Time to a decision"
          value={t.medianDecisionHours == null ? '—' : t.medianDecisionHours < 48 ? `${t.medianDecisionHours.toFixed(1)} h` : `${(t.medianDecisionHours / 24).toFixed(1)} d`}
          hint={`median from proposed to accepted or rejected, last ${days} days`}
        />
      </div>

      <QueueCard stats={stats} />

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Autopilot policies</CardTitle>
          <CardDescription>
            Each policy accepts every pending proposal of its kind at or above its confidence. The defaults are where October
            spot-checks found 19–25 of 25 right. The precision column is what people decided when they looked one by one:
            if it drops for a kind, raise its threshold or switch it off.
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Proposal</TableHead>
                <TableHead>Autopilot</TableHead>
                <TableHead>Threshold</TableHead>
                <TableHead>Waiting, by confidence</TableHead>
                <TableHead className="text-right">Waiting</TableHead>
                <TableHead className="text-right">Takes now</TableHead>
                <TableHead className="text-right">Held</TableHead>
                <TableHead className="text-right">By autopilot</TableHead>
                <TableHead className="text-right">Precision</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stats.policies.map((p) => <PolicyRow key={p.action} policy={p} stats={stats} />)}
              {stats.byAction.filter((a) => a.action === 'link' || a.action === 'split').map((a) => (
                <TableRow key={a.action} className="text-slate-500">
                  <TableCell>
                    <Link to={`/app/knowledge/proposals?action=${a.action}`} className="font-medium hover:underline">{ACTION_LABEL[a.action]}</Link>
                    <div className="text-[11px]">reviewed one by one — too noisy to accept unseen</div>
                  </TableCell>
                  <TableCell colSpan={3} className="text-xs">no policy</TableCell>
                  <TableCell className="text-right tabular-nums">{num(a.pending)}</TableCell>
                  <TableCell className="text-right">—</TableCell>
                  <TableCell className="text-right">—</TableCell>
                  <TableCell className="text-right">—</TableCell>
                  <TableCell className="text-right tabular-nums">{pct(a.precision)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="flex-row items-start justify-between space-y-0 pb-2">
            <div className="space-y-1.5">
              <CardTitle className="text-sm">Decisions per day</CardTitle>
              <CardDescription>Who decided the model&apos;s proposals each day; the dashed line is what it proposed.</CardDescription>
            </div>
            <select className="rounded border bg-white px-2 py-1 text-xs" value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Window">
              {[7, 14, 30, 90].map((d) => <option key={d} value={d}>Last {d} days</option>)}
            </select>
          </CardHeader>
          <CardContent>
            <DailyBars
              rows={stats.daily}
              series={(Object.keys(OUTCOME) as Array<keyof typeof OUTCOME>).map((k) => ({ key: k, ...OUTCOME[k] }))}
              line={{ key: 'proposed', label: 'Proposed', color: '#64748b' }}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">All decisions to date</CardTitle>
            <CardDescription>{num(decided)} proposals decided.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {(Object.keys(OUTCOME) as Array<keyof typeof OUTCOME>).map((k) => (
              <div key={k} className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="inline-flex items-center gap-1.5 text-slate-600">
                    <span className="h-2 w-2 rounded-sm" style={{ background: OUTCOME[k].color }} />
                    {OUTCOME[k].label}
                  </span>
                  <span className="tabular-nums text-slate-900">{num(accepted[k])} · {pct(decided ? accepted[k] / decided : null)}</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded bg-slate-100">
                  <div className="h-full rounded" style={{ width: `${decided ? (100 * accepted[k]) / decided : 0}%`, background: OUTCOME[k].color }} />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">By kind of proposal</CardTitle>
          <CardDescription>Stale proposals were overtaken by a build (their concepts merged or retired) before anyone decided.</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Proposal</TableHead>
                <TableHead className="text-right">Waiting</TableHead>
                <TableHead className="text-right">One by one</TableHead>
                <TableHead className="text-right">In bulk</TableHead>
                <TableHead className="text-right">Autopilot</TableHead>
                <TableHead className="text-right">Rejected</TableHead>
                <TableHead className="text-right">Stale</TableHead>
                <TableHead className="text-right">Precision</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stats.byAction.map((a) => (
                <TableRow key={a.action}>
                  <TableCell>
                    <Link to={`/app/knowledge/proposals?action=${a.action}`} className="hover:underline">{ACTION_LABEL[a.action]}</Link>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{num(a.pending)}</TableCell>
                  <TableCell className="text-right tabular-nums">{num(a.person)}</TableCell>
                  <TableCell className="text-right tabular-nums">{num(a.bulk)}</TableCell>
                  <TableCell className="text-right tabular-nums">{num(a.autopilot)}</TableCell>
                  <TableCell className="text-right tabular-nums">{num(a.rejected)}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-400">{num(a.stale)}</TableCell>
                  <TableCell className="text-right tabular-nums">{pct(a.precision)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Runs</CardTitle>
            <CardDescription>The latest 30 resolution runs. A run whose calibration fails keeps none of its proposals.</CardDescription>
          </CardHeader>
          <CardContent className="max-h-[420px] overflow-auto p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Run</TableHead>
                  <TableHead className="text-right">Verdicts</TableHead>
                  <TableHead className="text-right">Proposals</TableHead>
                  <TableHead className="text-right">Spent</TableHead>
                  <TableHead className="text-right">Calibration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.runs.map((r) => {
                  const idle = !r.candidates && !r.verdicts && r.status === 'ok'
                  return (
                    <TableRow key={r.runId}>
                      <TableCell>
                        <div className="text-sm">{r.startedAt ? formatDistanceToNow(new Date(r.startedAt), { addSuffix: true }) : r.runId}</div>
                        <div className="font-mono text-[10px] text-slate-400">{r.model} · {idle ? 'nothing new to judge' : resolutionStatus(r).replace(/_/g, ' ')}</div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{num(r.verdicts)}</TableCell>
                      <TableCell className="text-right tabular-nums">{num(r.proposals)}</TableCell>
                      <TableCell className="text-right tabular-nums">{usd(r.spentUsd)}</TableCell>
                      <TableCell className={`text-right tabular-nums ${r.calibrationPassed ? 'text-emerald-700' : r.verdicts ? 'text-rose-700' : 'text-slate-400'}`}>
                        {r.calibrationCatch == null ? '—' : `${pct(r.calibrationCatch)} ${r.calibrationPassed ? '✓' : '✗'}`}
                      </TableCell>
                    </TableRow>
                  )
                })}
                {stats.runs.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="py-6 text-center text-sm text-slate-500">No runs have reached this environment.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Accept jobs</CardTitle>
            <CardDescription>Background accepts, started by autopilot or by a person.</CardDescription>
          </CardHeader>
          <CardContent className="max-h-[420px] overflow-auto p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Started</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead className="text-right">Accepted</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(jobs?.data ?? stats.jobs).map((j) => (
                  <TableRow key={j.id}>
                    <TableCell className="text-sm">{formatDistanceToNow(new Date(j.createdAt), { addSuffix: true })}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        <span className="h-2 w-2 rounded-sm" style={{ background: j.origin === 'autopilot' ? OUTCOME.autopilot.color : OUTCOME.bulk.color }} />
                        {j.origin === 'autopilot' ? 'autopilot' : 'a person'}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{ACTION_LABEL[j.action]} ≥ {j.minConfidence.toFixed(2)}</TableCell>
                    <TableCell className="text-right tabular-nums">{num(j.accepted)} / {num(j.total)}</TableCell>
                    <TableCell className={`text-xs ${j.status === 'failed' ? 'text-rose-700' : 'text-slate-600'}`}>{j.status}</TableCell>
                  </TableRow>
                ))}
                {(jobs?.data ?? stats.jobs).length === 0 && (
                  <TableRow><TableCell colSpan={5} className="py-6 text-center text-sm text-slate-500">No accept jobs yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </KnowledgeFrame>
  )
}
